import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../lib/axios';
import Header from '../components/Header';

const AdminBlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  // Form state
  const [titulo, setTitulo] = useState('');
  const [slug, setSlug] = useState('');
  const [categoria, setCategoria] = useState('');
  const [resumo, setResumo] = useState('');
  const [conteudo, setConteudo] = useState('');
  const [imagemCapa, setImagemCapa] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [tempoLeitura, setTempoLeitura] = useState(0);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [publicado, setPublicado] = useState(false);
  const [destaque, setDestaque] = useState(false);

  // UI state
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('success');

  // Helper function to slugify text
  const slugify = (text) => {
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
  };

  // Calculate reading time
  const calculateReadingTime = (text) => {
    const wordCount = text.trim().split(/\s+/).length;
    return Math.ceil(wordCount / 200);
  };

  // Handle title change and auto-generate slug
  const handleTituloChange = (e) => {
    const newTitulo = e.target.value;
    setTitulo(newTitulo);
    setSlug(slugify(newTitulo));
  };

  // Handle conteudo change and auto-calculate reading time
  const handleConteudoChange = (e) => {
    const newConteudo = e.target.value;
    setConteudo(newConteudo);
    setTempoLeitura(calculateReadingTime(newConteudo));
  };

  // Handle tag input
  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!tags.includes(tagInput.trim())) {
        setTags([...tags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  // Remove tag
  const removeTag = (index) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  // Show message toast
  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(null), 5000);
  };

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategorias = async () => {
      try {
        const { data } = await api.get('/api/blog/categorias');
        setCategorias(Array.isArray(data) ? data : data.categorias || []);
      } catch (error) {
        console.error('Erro ao buscar categorias:', error);
        showMessage('Erro ao buscar categorias', 'error');
      }
    };
    fetchCategorias();
  }, []);

  // Fetch article data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchArticle = async () => {
        try {
          const { data } = await api.get(`/api/blog/artigos/${id}`);
          const art = data.artigo || data;
          setTitulo(art.titulo || '');
          setSlug(art.slug || '');
          setCategoria(art.categoria_id || '');
          setResumo(art.resumo || '');
          setConteudo(art.conteudo || '');
          setImagemCapa(art.imagem_capa || '');
          setTags(art.tags || []);
          setTempoLeitura(art.tempo_leitura || 0);
          setSeoTitle(art.seo_title || '');
          setSeoDescription(art.seo_description || '');
          setPublicado(art.publicado || false);
          setDestaque(art.destaque || false);
          setLoading(false);
        } catch (error) {
          console.error('Erro ao buscar artigo:', error);
          showMessage('Erro ao carregar artigo', 'error');
          setLoading(false);
        }
      };
      fetchArticle();
    }
  }, [isEditMode, id]);

  // Handle save draft
  const handleSaveDraft = async () => {
    if (!titulo.trim()) {
      showMessage('Título é obrigatório', 'error');
      return;
    }

    setSaving(true);
    const articleData = {
      titulo,
      slug,
      categoria_id: categoria,
      resumo,
      conteudo,
      imagem_capa: imagemCapa,
      tags,
      tempo_leitura: tempoLeitura,
      seo_title: seoTitle,
      seo_description: seoDescription,
      publicado: false,
      destaque,
    };

    try {
      if (isEditMode) {
        await api.put(`/api/blog/artigos/${id}`, articleData);
        showMessage('Rascunho salvo com sucesso');
      } else {
        await api.post('/api/blog/artigos', articleData);
        showMessage('Rascunho salvo com sucesso');
      }
      setTimeout(() => navigate('/admin/blog'), 1500);
    } catch (error) {
      console.error('Erro ao salvar rascunho:', error);
      showMessage('Erro ao salvar rascunho', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handle publish
  const handlePublish = async () => {
    if (!titulo.trim()) {
      showMessage('Título é obrigatório', 'error');
      return;
    }
    if (!categoria) {
      showMessage('Categoria é obrigatória', 'error');
      return;
    }
    if (!conteudo.trim()) {
      showMessage('Conteúdo é obrigatório', 'error');
      return;
    }

    setSaving(true);
    const articleData = {
      titulo,
      slug,
      categoria_id: categoria,
      resumo,
      conteudo,
      imagem_capa: imagemCapa,
      tags,
      tempo_leitura: tempoLeitura,
      seo_title: seoTitle,
      seo_description: seoDescription,
      publicado: true,
      destaque,
    };

    try {
      if (isEditMode) {
        await api.put(`/api/blog/artigos/${id}`, articleData);
        showMessage('Artigo publicado com sucesso');
      } else {
        await api.post('/api/blog/artigos', articleData);
        showMessage('Artigo publicado com sucesso');
      }
      setTimeout(() => navigate('/admin/blog'), 1500);
    } catch (error) {
      console.error('Erro ao publicar artigo:', error);
      showMessage('Erro ao publicar artigo', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-600">Carregando artigo...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Message Toast */}
      {message && (
        <div
          className={`fixed top-20 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg text-white z-50 ${
            messageType === 'success' ? 'bg-green-500' : 'bg-red-500'
          }`}
        >
          {message}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-blue-900 mb-2">
            {isEditMode ? 'Editar Artigo' : 'Novo Artigo'}
          </h1>
          <p className="text-gray-600">
            {isEditMode
              ? 'Edite os detalhes do seu artigo'
              : 'Crie um novo artigo do blog'}
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column (2/3) */}
          <div className="col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-blue-900 mb-2">
                Título *
              </label>
              <input
                type="text"
                value={titulo}
                onChange={handleTituloChange}
                placeholder="Digite o título do artigo"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
              />
            </div>

            {/* Slug */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-blue-900 mb-2">
                Slug
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="url-slug-do-artigo"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-2">
                Auto-gerado a partir do título (editável)
              </p>
            </div>

            {/* Resumo */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-semibold text-blue-900">
                  Resumo
                </label>
                <span className="text-xs text-gray-500">
                  {resumo.length}/200
                </span>
              </div>
              <textarea
                value={resumo}
                onChange={(e) => setResumo(e.target.value.slice(0, 200))}
                placeholder="Breve resumo do artigo"
                maxLength={200}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
              />
            </div>

            {/* Conteudo */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-blue-900 mb-2">
                Conteúdo *
              </label>
              <textarea
                value={conteudo}
                onChange={handleConteudoChange}
                placeholder="Digite ou cole o conteúdo HTML do artigo"
                rows={12}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-2">
                Suporta HTML. Tempo de leitura calculado automaticamente:{' '}
                <strong>{tempoLeitura} min</strong>
              </p>
            </div>
          </div>

          {/* Right Column (1/3) */}
          <div className="col-span-1 space-y-6">
            {/* Categoria */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-blue-900 mb-2">
                Categoria *
              </label>
              <select
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
              >
                <option value="">Selecione uma categoria</option>
                {categorias.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Imagem de Capa */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-blue-900 mb-2">
                Imagem de Capa (URL)
              </label>
              <input
                type="text"
                value={imagemCapa}
                onChange={(e) => setImagemCapa(e.target.value)}
                placeholder="https://exemplo.com/imagem.jpg"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
              />
              {imagemCapa && (
                <div className="mt-4">
                  <img
                    src={imagemCapa}
                    alt="Preview"
                    className="w-full rounded-lg max-h-48 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-blue-900 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    {tag}
                    <button
                      onClick={() => removeTag(index)}
                      className="text-orange-800 hover:text-orange-900 font-bold"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagKeyPress}
                placeholder="Digite uma tag e pressione Enter"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
              />
            </div>

            {/* Tempo de Leitura */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-blue-900 mb-2">
                Tempo de Leitura (minutos)
              </label>
              <input
                type="number"
                value={tempoLeitura}
                onChange={(e) => setTempoLeitura(parseInt(e.target.value) || 0)}
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
              />
              <p className="text-xs text-gray-500 mt-2">
                Calculado automaticamente (editável)
              </p>
            </div>

            {/* SEO Title */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-blue-900 mb-2">
                SEO - Título
              </label>
              <input
                type="text"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                placeholder="Título para buscadores"
                maxLength={60}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Máx. 60 caracteres</p>
            </div>

            {/* SEO Description */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-blue-900 mb-2">
                SEO - Descrição
              </label>
              <textarea
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value.slice(0, 160))}
                placeholder="Descrição para buscadores"
                maxLength={160}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                {seoDescription.length}/160 caracteres
              </p>
            </div>

            {/* Checkboxes */}
            <div className="bg-white rounded-lg shadow p-6 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={publicado}
                  onChange={(e) => setPublicado(e.target.checked)}
                  className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                />
                <span className="text-sm font-medium text-blue-900">
                  Publicado
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={destaque}
                  onChange={(e) => setDestaque(e.target.checked)}
                  className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                />
                <span className="text-sm font-medium text-blue-900">
                  Destaque
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex gap-4 justify-end">
          <Link
            to="/admin/blog"
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            Cancelar
          </Link>
          <button
            onClick={handleSaveDraft}
            disabled={saving}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Salvar Rascunho'}
          </button>
          <button
            onClick={handlePublish}
            disabled={saving}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition disabled:opacity-50"
          >
            {saving ? 'Publicando...' : 'Publicar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminBlogEditor;
